import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import {
    Document,
    Font,
    Image,
    PDFViewer,
    Page,
    StyleSheet,
    Text,
    View,
} from '@react-pdf/renderer';
import { IPagamentoRow } from 'pages/pagamento/pagamento.model';
import {
    FlowVendaTO,
    StatusPedido,
    TFlowProdutoImpressaoTO,
    TipoDesconto,
} from 'pages/registra-venda/registra-venda.model';
import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { currencyOf, isEmpty, roundHalfToEven, showOnlyDate } from 'utils/util';

import { customColors, theme } from 'theme';
import { getValorAPagar, getValorDesconto, getValorTotalBrindes, getVTBNormalizado, possuiBrinde } from 'utils/impressao-utils';
import workSansBold from './../../assets/fonts/WorkSans-Bold.ttf';

type TInitialState = {
    flowVendaTO: FlowVendaTO;
};

const initialState: TInitialState = {
    flowVendaTO: {
        uuid: '',
        valorTotalBruto: 0,
        valorTotalLiquido: 0,
        valorTotalLiquidoTroca: 0,
        quantidadeTotal: 0,
        quantidadeTotalTroca: 0,
        emissao: '',
        entrega: '',
        numero: '',
        numeroEntrega: 0,
        cliente: null,
        vendedorPrincipal: null,
        outrosVendedores: [],
        guias: [],
        formaComissao: null,
        finalidadePedido: null,
        parcelas: [],
        condicoesPagamento: [],
        flowItensTroca: [],
        itensPagamento: [],
        itensPagamentoDevolucao: [],
        itens: [],
        itensTroca: [],
        cpfNota: null,
        pedidoDescontoAcrescimo: {
            uuidAutorizador: '',
            valorDesconto: 0,
            percentualDesconto: 0,
            tipoDesconto: TipoDesconto.PERCENTUAL,
            acrescimo: 0,
        },
        tabelaPreco: null,
        deposito: null,
        statusPedido: StatusPedido.ABERTO,
        consultados: [],
        estabelecimento: {
            nomeFantasia: '',
            telefone: '',
            endereco: '',
            inscricaoEstadual: '',
            complemento: '',
            municipio: '',
            email: '',
            cep: '',
        },
        flowItens: {
            produtos: [],
            condicaoPagamentos: [],
            dadosCartaoTOS: [],
            dadosChequeTOS: [],
        },
        transportador: {
            frete: {
                codigo: '',
                descricao: '',
            },
            transportadora: {
                descritivo: '',
                uuid: '',
            },
            placa: '',
            uf: '',
            pesoLiquido: null,
            pesoBruto: null,
            quantidade: null,
            especie: '',
            marca: '',
            numeroVolumes: null,
            valorFrete: null,
            valorSeguro: null,
        },
        parcelasCrediario: [],
        observacao: '',
        observacaoFixa: '',
        troco: null,
        canalDeVenda: null,
        pedidosAgrupados: [],
    },
};

const getCurrentTime = (): string => {
    const now: Date = new Date();
    const hours: number = now.getHours();
    const minutes: number = now.getMinutes();

    const formattedHours: string = hours < 10 ? '0' + hours : hours.toString();
    const formattedMinutes: string = minutes < 10 ? '0' + minutes : minutes.toString();

    return `${formattedHours}:${formattedMinutes}`;
};

const RomaneioVenda = (props: { dados: FlowVendaTO; onGoBack: () => void; }) => {
    const userInfo = useAppSelector(e => e.token.USER_INFO);
    const { descritivo: estabelecimentoDescritivo } = useAppSelector(
        e => e.estabelecimentoSelecionado,
    );
    const [stateLocal, setStateLocal] = useState(initialState);

    useEffect(() => {
        setStateLocal(prevState => ({ ...prevState, flowVendaTO: props.dados }));
        //eslint-disable-next-line
    }, []);

    const toLocalDateString = useCallback((str: number, maxFract?: number) => {
        if (str === null || str === undefined || Number.isNaN(str)) {
            return null;
        }
        return str.toLocaleString('pt-BR', {
            minimumFractionDigits: maxFract ?? 2,
            maximumFractionDigits: maxFract ?? 2,
        });
    }, []);

    const header = () => {
        return (
            <View
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                }}>
                <Text style={[styles.minText, styles.bold]}>
                    Estabelecimento:
                    <Text
                        style={{
                            fontWeight: 'normal',
                        }}>
                        {estabelecimentoDescritivo}
                    </Text>
                </Text>

                <Text style={[styles.minText]}>
                    Impresso por {userInfo?.username} em{' '}
                    {stateLocal.flowVendaTO.emissao ? showOnlyDate(new Date().toString()) : null} às{' '}
                    {getCurrentTime()}
                </Text>
            </View>
        );
    };

    const getTitulo = (data: {
        title: string;
        bigFontSize?: boolean;
        visible?: boolean;
        centralizado?: boolean;
    }) => {
        data.visible = data.visible === undefined ? true : data.visible;
        return data.visible ?
            <View
                style={{
                    borderTop: '1px solid black',
                    borderBottom: '1px solid black',
                    backgroundColor: customColors.cinzaClaro,
                    alignItems: data.centralizado ? 'center' : 'flex-start',
                }}>
                <View>
                    <Text style={[styles.bold, { fontSize: data.bigFontSize ? 14 : 10 }]}>
                        {data.title}
                    </Text>
                </View>
            </View>
            : null;
    };

    const renderTamanhos = (produtos: TFlowProdutoImpressaoTO) => {
        return (
            <View style={{ flexDirection: 'column' }}>
                <View
                    style={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.palette.background.default,
                    }}>
                    {produtos.tamanhos.map((tamanhoUnico, index) => (
                        <Text
                            key={`tamanho-${index}`}
                            style={[
                                styles.minText,
                                {
                                    width: 4 * tamanhoUnico.tamanho.length + 18,
                                    textAlign: 'center',
                                    padding: 0,
                                    alignSelf: 'center',
                                },
                            ]}>
                            {tamanhoUnico.tamanho}
                        </Text>
                    ))}
                </View>
                {produtos.cores.map((cor, index) => (
                    <View
                        key={`cor-${cor.cor}-${index}`}
                        style={{ flexDirection: 'row' }}>
                        {produtos.tamanhos.map((tamanhoUnico, idx) => {
                            return (
                                <Text
                                    key={`quantidade-${tamanhoUnico.tamanho}-${idx}`}
                                    style={[
                                        styles.minText,
                                        {
                                            width: 4 * tamanhoUnico.tamanho.length + 18,
                                            textAlign: 'center',
                                            alignSelf: 'center',
                                        },
                                    ]}>
                                    {cor.tamanhos.find(tam => tam.tamanho === tamanhoUnico.tamanho)
                                        ?.quantidade ?? 0}
                                </Text>
                            );
                        })}
                    </View>
                ))}
            </View>
        );
    };

    const pedidoLeftSide = () => {
        const guias = stateLocal.flowVendaTO.guias
            .map(outroVendedor => outroVendedor.descritivo.split(' - ')[1]);

        return (
            <View style={{ width: '50%' }}>
                <View style={[{ height: 'auto', flexDirection: 'row' }]}>
                    <Text style={[styles.minText, { width: '50%' }]}>
                        Pedido:{' '}
                        {`${stateLocal.flowVendaTO.numero.length ? stateLocal.flowVendaTO.numero : '0'}/${stateLocal.flowVendaTO.numeroEntrega}`}
                    </Text>
                    <Text style={[styles.minText]}>
                        Status:{' '}
                        {stateLocal.flowVendaTO.statusPedido ?
                            `${stateLocal.flowVendaTO.statusPedido}`
                            : null}
                    </Text>
                </View>
                <Text style={[styles.minText]}>
                    Cliente:{' '}
                    {`${stateLocal.flowVendaTO.cliente?.descritivo.substring(0, 60).concat('...') ?? 'CONSUMIDOR'}`}
                </Text>
                <Text style={[styles.minText]}>
                    Nome fantasia:{' '}
                    {stateLocal.flowVendaTO.estabelecimento.nomeFantasia ?
                        `${stateLocal.flowVendaTO.estabelecimento.nomeFantasia}`
                        : null}
                </Text>
                <Text style={[styles.minText]}>
                    Endereço:{' '}
                    {stateLocal.flowVendaTO.estabelecimento.endereco ?
                        `${stateLocal.flowVendaTO.estabelecimento.endereco}`
                        : null}
                </Text>
                <Text style={[styles.minText]}>
                    Complemento:{' '}
                    {stateLocal.flowVendaTO.estabelecimento.complemento ?
                        `${stateLocal.flowVendaTO.estabelecimento.complemento}`
                        : null}
                </Text>
                <Text style={[styles.minText]}>
                    E-mail:{' '}
                    {stateLocal.flowVendaTO.estabelecimento.email ?
                        `${stateLocal.flowVendaTO.estabelecimento.email}`
                        : null}
                </Text>
                <Text style={[styles.minText]}>
                    Representante:{' '}
                    {!!stateLocal.flowVendaTO.vendedorPrincipal ?
                        `${stateLocal.flowVendaTO.vendedorPrincipal.descritivo.split(' - ')[1]}`
                        : null}
                </Text>
                <Text style={[styles.minText]}>
                    Guias: {guias.length > 0 ? guias.join(', ') : null}
                </Text>
                <Text style={[styles.minText]}>
                    Canal de venda: {stateLocal.flowVendaTO.canalDeVenda?.descritivo ?? null}
                </Text>
            </View>
        );
    };

    const pedidoRightSide = () => {
        return (
            <View style={{ width: '50%' }}>
                <View style={{ height: 'auto', flexDirection: 'row' }}>
                    <Text style={[styles.minText, { width: '50%' }]}>
                        Emissão:{' '}
                        {stateLocal.flowVendaTO.emissao ?
                            showOnlyDate(stateLocal.flowVendaTO.emissao.toString())
                            : null}
                    </Text>
                    <Text style={[styles.minText]}>
                        Entrega:{' '}
                        {stateLocal.flowVendaTO.entrega ?
                            showOnlyDate(stateLocal.flowVendaTO.entrega.toString())
                            : null}
                    </Text>
                </View>
                <Text style={[styles.minText]}>
                    Tabela de preço:{' '}
                    {stateLocal.flowVendaTO.tabelaPreco?.descritivo ?
                        `${stateLocal.flowVendaTO.tabelaPreco?.descritivo ?? null}`
                        : null}
                </Text>
                <Text style={[styles.minText]}>
                    Telefone: {stateLocal.flowVendaTO.estabelecimento.telefone ?? null}
                </Text>
                <Text style={[styles.minText]}>
                    Inscrição estadual:{' '}
                    {stateLocal.flowVendaTO.estabelecimento.inscricaoEstadual ?? null}
                </Text>
                <Text style={[styles.minText]}>
                    Município: {stateLocal.flowVendaTO.estabelecimento.municipio ?? null}
                </Text>
                <Text style={[styles.minText]}>
                    CEP: {stateLocal.flowVendaTO.estabelecimento.cep ?? null}
                </Text>
                <Text style={[styles.minText]}>
                    Forma de comissão: {stateLocal.flowVendaTO.formaComissao?.descritivo ?? null}
                </Text>
                <Text style={[styles.minText]}>
                    Website: {stateLocal.flowVendaTO.cliente?.website ?? null}
                </Text>
                <Text style={[styles.minText]}>
                    Rede social: {stateLocal.flowVendaTO.cliente?.redeSocial ?? null}
                </Text>
            </View>
        );
    };

    const getValorTotal = useCallback(
        (valorUnitario: string, quantidade: number, desconto: string) => {
            valorUnitario = valorUnitario.replace(',', '.');
            desconto = desconto.replace(',', '.');
            return Number(valorUnitario) * quantidade - Number(desconto);
        },
        [],
    );

    const renderProdutos = (
        itens: TFlowProdutoImpressaoTO[] = stateLocal.flowVendaTO.flowItens.produtos,
    ) => {
        return itens.map((e, i) => {
            return (
                <View
                    key={`${e.nome}-${i}`}
                    wrap={false}
                    style={{
                        marginTop: i > 0 ? 5 : 0,
                        borderBottom:
                            i === stateLocal.flowVendaTO.flowItens.produtos.length - 1 ?
                                undefined
                                : '1px solid black',
                    }}>
                    <Text
                        style={[
                            styles.minText,
                            { backgroundColor: theme.palette.background.default, padding: 0 },
                        ]}>
                        {e.nome}
                    </Text>
                    <View style={{ flexDirection: 'row' }}>
                        <View style={{ width: '50%' }}>
                            <View style={{ flexDirection: 'row' }}>
                                <Text style={[styles.minText, { width: '44%' }]}>Cor</Text>
                                <Text
                                    style={[styles.minText, { width: '14%', textAlign: 'center' }]}>
                                    Valor Unitário
                                </Text>
                                <Text
                                    style={[styles.minText, { width: '14%', textAlign: 'center' }]}>
                                    Desconto
                                </Text>
                                <Text
                                    style={[styles.minText, { width: '14%', textAlign: 'center' }]}>
                                    Valor Total
                                </Text>
                                <View style={{ width: '14%' }}>
                                    <Text
                                        style={[
                                            styles.minText,
                                            { textAlign: 'center', paddingRight: 5 },
                                        ]}>
                                        Qtd total
                                    </Text>
                                </View>
                            </View>
                            {e.cores.map((cor, idx) => {
                                return (
                                    <View
                                        key={`${cor.cor}-${idx}`}
                                        style={{ flexDirection: 'row' }}>
                                        <Text style={[styles.minText, { width: '44%' }]}>
                                            {cor.cor}
                                        </Text>
                                        <Text
                                            style={[
                                                styles.minText,
                                                { width: '14%', textAlign: 'center' },
                                            ]}>
                                            {cor.valorUnitario}
                                        </Text>
                                        <Text
                                            style={[
                                                styles.minText,
                                                { width: '14%', textAlign: 'center' },
                                            ]}>
                                            {cor.desconto}
                                        </Text>
                                        <Text
                                            style={[
                                                styles.minText,
                                                { width: '14%', textAlign: 'center' },
                                            ]}>
                                            {toLocalDateString(
                                                getValorTotal(
                                                    cor.valorUnitario,
                                                    cor.quantidade,
                                                    cor.desconto,
                                                ),
                                            )}
                                        </Text>
                                        <Text
                                            style={[
                                                styles.minText,
                                                { width: '14%', textAlign: 'center' },
                                            ]}>
                                            {cor.quantidade}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                        <View style={{ width: '50%', flexDirection: 'row' }}>
                            {renderTamanhos(itens[i])}
                        </View>
                    </View>
                </View>
            );
        });
    };

    const renderDevolucao = () => {
        return renderProdutos(stateLocal.flowVendaTO.flowItensTroca);
    };

    const hasDevolucao = () => {
        return !isEmpty(stateLocal.flowVendaTO.flowItensTroca);
    };

    const getValorTotalLiquidoRomaneio = () =>
        roundHalfToEven(getValorAPagar(stateLocal.flowVendaTO), 2);

    const renderTotalizador = () => {
        return (
            <>
                <View
                    style={{
                        borderTop: '1px solid black',
                        alignItems: 'center',
                        backgroundColor: customColors.cinzaClaro,
                    }}
                />
                <View
                    style={{
                        flexDirection: 'row',
                    }}>
                    <View
                        style={{
                            width: '80%',
                            height: '100%',
                        }}></View>

                    <View
                        style={{
                            width: '20%',
                            height: '100%',
                        }}>
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Quantidade total:</Text>
                            <Text>
                                {stateLocal.flowVendaTO.itens.reduce(
                                    (prev, act) => (prev += act.quantidade),
                                    0,
                                )}
                            </Text>
                        </View>
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Valor total:</Text>
                            <Text>
                                {toLocalDateString(getVTBNormalizado(stateLocal.flowVendaTO))}
                            </Text>
                        </View>
                        {possuiBrinde(stateLocal.flowVendaTO.itens) && (
                            <View style={[styles.minText, styles.row]}>
                                <Text style={[styles.footerTitle]}>Brinde R$:</Text>
                                <Text>
                                    {toLocalDateString(getValorTotalBrindes(stateLocal.flowVendaTO.itens))}
                                </Text>
                            </View>
                        )}
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Desconto em R$:</Text>
                            <Text>
                                {toLocalDateString(getValorDesconto(stateLocal.flowVendaTO))}
                            </Text>
                        </View>
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Desconto em %:</Text>
                            <Text>
                                {toLocalDateString(
                                    stateLocal.flowVendaTO.pedidoDescontoAcrescimo
                                        .percentualDesconto,
                                )}
                            </Text>
                        </View>
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Acréscimo R$:</Text>
                            <Text>
                                {toLocalDateString(
                                    stateLocal.flowVendaTO.pedidoDescontoAcrescimo.acrescimo,
                                )}
                            </Text>
                        </View>
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Devoluções R$</Text>
                            <Text>
                                {toLocalDateString(stateLocal.flowVendaTO.valorTotalLiquidoTroca)}
                            </Text>
                        </View>
                        {!!stateLocal.flowVendaTO.transportador?.valorFrete && (
                            <View style={[styles.minText, styles.row]}>
                                <Text style={[styles.footerTitle]}>Valor frete:</Text>
                                <Text>
                                    {toLocalDateString(
                                        stateLocal.flowVendaTO.transportador?.valorFrete,
                                    )}
                                </Text>
                            </View>
                        )}
                        {!!stateLocal.flowVendaTO.transportador?.valorSeguro && (
                            <View style={[styles.minText, styles.row]}>
                                <Text style={[styles.footerTitle]}>Valor seguro:</Text>
                                <Text>
                                    {toLocalDateString(
                                        stateLocal.flowVendaTO.transportador?.valorSeguro,
                                    )}
                                </Text>
                            </View>
                        )}
                        <View style={[styles.minText, styles.row]}>
                            <Text style={[styles.footerTitle]}>Valor Liquido:</Text>
                            <Text>{toLocalDateString(getValorTotalLiquidoRomaneio())}</Text>
                        </View>
                    </View>
                </View>
            </>
        );
    };

    const renderObservacoes = () => {
        return (
            <>
                {!!stateLocal.flowVendaTO.observacaoFixa && (
                    <View
                        style={{
                            flexDirection: 'row',
                            border: '1px solid black',
                            borderRadius: 2,
                            padding: 2,
                        }}>
                        <View
                            style={{
                                width: '101.2%',
                                height: '100%',
                                marginBottom: '6px',
                            }}>
                            <Text style={[styles.minText]}>Observação fixa:</Text>
                            <Text style={[styles.minText]}>
                                {stateLocal.flowVendaTO.observacaoFixa}
                            </Text>
                        </View>
                    </View>
                )}
                {!!stateLocal.flowVendaTO.observacao && (
                    <View
                        style={{
                            flexDirection: 'row',
                            border: '1px solid black',
                            borderRadius: 2,
                            padding: 2,
                        }}>
                        <View
                            style={{
                                width: '100%',
                                height: '100%',
                                marginBottom: 2,
                            }}>
                            <Text style={[styles.minText]}>Observação:</Text>
                            <Text style={[styles.minText]}>
                                {stateLocal.flowVendaTO.observacao}
                            </Text>
                        </View>
                    </View>
                )}
            </>
        );
    };

    const calculateTotalValuesPagamentos = (): string[] => {
        const pagamentos = stateLocal.flowVendaTO.itensPagamento;
        const totalValues = new Map<string, { totalValue: number; count: number; }>();
        const dadosCartoes: IPagamentoRow[] = [];

        pagamentos.forEach(s => {
            const { descritivoFormaPagamento, valor } = s;
            if (descritivoFormaPagamento.includes('CARTÃO DE')) {
                dadosCartoes.push(s);
            } else {
                const currentTotal = totalValues.get(descritivoFormaPagamento);
                if (currentTotal) {
                    currentTotal.totalValue += valor;
                    currentTotal.count++;
                } else {
                    totalValues.set(descritivoFormaPagamento, {
                        totalValue: valor,
                        count: 1,
                    });
                }
            }
        });

        if (totalValues.has('CHEQUE')) {
            const chequeData = totalValues.get('CHEQUE');
            if (chequeData) {
                totalValues.delete('CHEQUE'); // Remove it temporarily
                totalValues.set('CHEQUE', chequeData); // Reinsert it at the end
            }
        }

        const toReturn = Array.from(totalValues.entries()).map(
            ([descritivo, { totalValue, count }]) => {
                return `${descritivo} ${count} X - ${currencyOf(totalValue)}`;
            },
        );

        dadosCartoes.forEach(s => {
            const dadosCartao = s.infoAdicional?.dadosCartao;
            const descritivo =
                !!dadosCartao?.modoPagamento?.modoPagamento ?
                    `CARTÃO DE ${dadosCartao?.modoPagamento?.modoPagamento}`
                    : s.descritivoFormaPagamento;

            toReturn.push(
                `${descritivo} - ${currencyOf(s.valor)}     Bandeira: ${dadosCartao?.bandeiraCartao?.descritivo ?? '--'}      Nsu: ${dadosCartao?.nsu ?? '--'}     Numero controle: ${dadosCartao?.numeroControle ?? '--'}`,
            );
        });

        return toReturn;
    };

    const getChequeDados = useCallback(() => {
        return (
            <View style={{ marginBottom: 5 }}>
                {stateLocal.flowVendaTO.flowItens.dadosChequeTOS.length > 0 && (
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={[styles.minText, { paddingTop: 2, width: '8.333%' }]}>
                            Valor
                        </Text>
                        <Text
                            style={[
                                styles.minText,
                                { paddingTop: 2, width: '33.333%', paddingLeft: 20 },
                            ]}>
                            Banco
                        </Text>
                        <Text style={[styles.minText, { paddingTop: 2, width: '8.333%' }]}>
                            Agencia
                        </Text>
                        <Text style={[styles.minText, { paddingTop: 2, width: '16.666%' }]}>
                            Numero
                        </Text>
                        <Text style={[styles.minText, { paddingTop: 2, width: '16.666%' }]}>
                            Emitente
                        </Text>
                        <Text style={[styles.minText, { paddingTop: 2, width: '16.666%' }]}>
                            Vencimento
                        </Text>
                    </View>
                )}
                {stateLocal.flowVendaTO.flowItens.dadosChequeTOS.map((e, key) => {
                    return (
                        <View
                            key={e.agencia + key}
                            style={{ flexDirection: 'row' }}>
                            <Text style={[styles.minText, { paddingTop: 2, width: '8.333%' }]}>
                                {currencyOf(e.valor)}
                            </Text>
                            <Text
                                style={[
                                    styles.minText,
                                    { paddingTop: 2, width: '33.333%', paddingLeft: 20 },
                                ]}>
                                {e.banco?.descritivo}
                            </Text>
                            <Text
                                style={[
                                    styles.minText,
                                    { paddingTop: 2, width: '8.333%', paddingLeft: 5 },
                                ]}>
                                {e.agencia}
                            </Text>
                            <Text style={[styles.minText, { paddingTop: 2, width: '16.666%' }]}>
                                {e.numeroCheque}
                            </Text>
                            <Text style={[styles.minText, { paddingTop: 2, width: '16.666%' }]}>
                                {e.emitente?.substring(0, 20)}
                                {e.emitente && e.emitente.length > 20 ?
                                    <Text>...</Text>
                                    : null}
                            </Text>
                            <Text style={[styles.minText, { paddingTop: 2, width: '16.666%' }]}>
                                {e.vencimento ? showOnlyDate(new Date(e.vencimento)) : null}
                            </Text>
                        </View>
                    );
                })}
            </View>
        );
    }, [stateLocal.flowVendaTO.flowItens.dadosChequeTOS]);

    const totalValues = calculateTotalValuesPagamentos();

    const renderPagamentos = () => {
        return (
            <View style={{ width: '100%' }}>
                <View style={{ flexDirection: 'column' }}>
                    <Text style={[styles.minText]}>Formas de pagamento: </Text>
                    {totalValues.map(descritivo => (
                        <Text
                            key={descritivo}
                            style={[styles.minText, { paddingTop: 2 }]}>
                            {descritivo || null}
                        </Text>
                    ))}
                </View>
            </View>
        );
    };

    return (
        <>
            <IconButton
                sx={{
                    zIndex: 100000,
                    position: 'absolute',
                    bottom: 20,
                    right: 30,
                    backgroundColor: theme.palette.primary.main,
                    ':hover': {
                        backgroundColor: theme.palette.primary.main,
                        filter: 'grayscale(.2) brightness(.8) contrast(1.5)',
                    },
                }}
                onClick={props.onGoBack}>
                <CloseIcon sx={{ color: theme.palette.background.default, fontSize: 45 }} />
            </IconButton>

            <PDFViewer
                style={{
                    zIndex: 10000,
                    width: '100%',
                    height: '100vh',
                    border: 0,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
                showToolbar={true}>
                <Document
                    title='Romaneio de venda'
                    author='Peon'
                    language='pt-BR'>
                    <Page
                        size={'A4'}
                        orientation='landscape'
                        wrap={true}
                        style={{
                            backgroundColor: theme.palette.background.default,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: '100vh',
                            padding: 20,
                            paddingBottom: 35,
                        }}
                        break>
                        <View>
                            {header()}
                            {getTitulo({
                                title: 'Pedido de venda',
                                bigFontSize: true,
                                centralizado: true,
                            })}
                            <View style={{ flexDirection: 'row' }}>
                                {pedidoLeftSide()}
                                {pedidoRightSide()}
                            </View>
                            {getTitulo({ title: 'Produtos' })}
                            {renderProdutos()}
                            {getTitulo({ title: 'Devoluções', visible: hasDevolucao() })}
                            {renderDevolucao()}
                            {getTitulo({ title: 'Pagamentos' })}
                            {renderPagamentos()}
                            {getChequeDados()}
                            {renderTotalizador()}
                            {renderObservacoes()}
                        </View>
                        <View
                            fixed
                            style={{
                                width: '95%',
                                position: 'absolute',
                                bottom: 10,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 10,
                            }}>
                            <View
                                style={{
                                    width: '100px',
                                    height: '25px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.primary.main,
                                    paddingHorizontal: 5,
                                    borderRadius: '5px',
                                }}>
                                <Image src={'assets/logoFlow.png'} />
                            </View>
                            <Text
                                style={{
                                    fontSize: 10,
                                    color: 'grey',
                                }}
                                render={({ pageNumber, totalPages }) =>
                                    `${pageNumber} / ${totalPages}`
                                }
                            />
                        </View>
                    </Page>
                </Document>
            </PDFViewer>
        </>
    );
};

export default RomaneioVenda;

Font.register({
    family: 'Work sans',
    src: workSansBold,
    fontWeight: 'bold',
});

const styles = StyleSheet.create({
    minText: {
        fontSize: '8px',
    },
    bold: {
        fontWeight: 'bold',
        fontFamily: 'Work sans',
    },
    oneOutOfEight: {
        flex: 1 / 8,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerTitle: {
        width: '50%',
        textAlign: 'right',
    },
});
